import { SessionTimeoutService } from '@formrails/shared/data-access';
import { Component, OnInit, ChangeDetectorRef, NgZone, inject, HostListener, signal, computed, effect, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { take, debounceTime } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { FormContextMenuComponent, UnsavedChangesDialogComponent } from '@formrails/form-builder/ui';
import { FormElementPaletteComponent, FormElement } from '@formrails/form-builder/ui';
import { QuestionView, QuestionType, FormStatus, Form, generateGuid, ApplicationTemplateDto, CanComponentDeactivate, FormStore, PROMOTION_TARGET, promotionTargetLabel } from '@formrails/form-builder/data-access';
import { QuestionContainerComponent } from '@formrails/form-builder/ui';
import { AddQuestionButtonComponent } from '@formrails/form-builder/ui';
import { FormSelectorModalComponent, FormComparePaneComponent } from '@formrails/form-builder/ui';
import { ConfirmationDialogComponent } from '@formrails/form-builder/ui';
import { FormHeaderComponent } from '@formrails/form-builder/ui';
import { FormToolbarComponent, AssessmentMatricesComponent } from '@formrails/form-builder/ui';
import { FormType } from '@formrails/shared/ui';
import { PromotionDto } from '@formrails/shared/data-access';
import {
  FormApiService,
  FormPersistenceService, FormSaveService, FormSaveContext, FormSaveResult,
  FormLoadService, FormLoadCallbacks, FormDragDropService,
  FormTemplateType,
  QuestionManagementService, QuestionManagementCallbacks,
  ReportableElementsService, QuestionNumberingService
} from '@formrails/form-builder/data-access';

@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [
    FormsModule,
    ToastModule,
    FormContextMenuComponent,
    FormElementPaletteComponent,
    QuestionContainerComponent,
    AddQuestionButtonComponent,
    FormSelectorModalComponent,
    FormComparePaneComponent,
    ConfirmationDialogComponent,
    FormHeaderComponent,
    FormToolbarComponent,
    AssessmentMatricesComponent,
    UnsavedChangesDialogComponent
  ],
  providers: [MessageService],
  templateUrl: './form-builder.component.html',
  styleUrl: './form-builder.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
class FormBuilderComponent implements OnInit, CanComponentDeactivate {
  private messageService = inject(MessageService);
  private formStore = inject(FormStore);
  private router = inject(Router);
  private formPersistenceService = inject(FormPersistenceService);
  private sessionTimeout = inject(SessionTimeoutService);
  private questionNumberingService = inject(QuestionNumberingService);
  private formApiService = inject(FormApiService);
  private formSaveService = inject(FormSaveService);
  private formLoadService = inject(FormLoadService);
  private dragDropService = inject(FormDragDropService);
  private questionManagement = inject(QuestionManagementService);
  private reportableElements = inject(ReportableElementsService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  // Store-synced signals (writable — services and callbacks write to these)
  readonly formTitle = signal('');
  readonly formDescription = signal('');
  readonly formVersion = signal(0);
  readonly showQuestionNumbers = signal(true);
  readonly formStatus = signal<FormStatus>(FormStatus.Draft);
  readonly formType = signal<FormType | null>(null);
  readonly questions = signal<QuestionView[]>([]);
  /** How far this version has travelled towards production; null until asked for. */
  readonly promotion = signal<PromotionDto | null>(null);

  /**
   * Where a promotion from this environment lands: sandbox from CI, production
   * from sandbox, nowhere from production. Fixed per build, so a plain field.
   */
  readonly promotionTarget = PROMOTION_TARGET;

  /**
   * Assessment matrices this application is assessed against. Application
   * templates only — see AssessmentMatricesComponent, which owns the picker.
   */
  readonly assessmentMatrixIds = signal<string[]>([]);
  readonly isReadOnly = signal(false);

  // Read-only store signal (the signal store exposes this directly)
  readonly loadingForm = this.formStore.loadingFormById;

  // Local component state as signals
  readonly showPalette = signal(false);
  readonly paletteQuestionId = signal<string | null>(null);
  readonly showFormSelector = signal(false);
  /** Whether the saved-forms drawer is opening a form or picking one to compare. */
  readonly formSelectorMode = signal<'open' | 'compare'>('open');
  /** Saved form shown read-only beside this one; null when not comparing. */
  readonly compareTemplate = signal<ApplicationTemplateDto | null>(null);
  readonly showClearConfirmation = signal(false);
  readonly showPublishConfirmation = signal(false);
  readonly showApprovePromotionConfirmation = signal(false);
  readonly showArchiveConfirmation = signal(false);
  readonly showFormTypeSelector = signal(false);
  readonly showUnsavedChangesDialog = signal(false);
  readonly showSessionRecoveryDialog = signal(false);

  // Session recovery state
  private sessionRecoveryInfo: { timestamp: string; formId: string; formTitle: string } | null = null;

  // Pending action to execute after unsaved changes dialog confirmation
  private pendingAction: (() => void) | null = null;

  readonly draggedQuestionId = signal<string | null>(null);
  readonly dragOverIndex = signal<number | null>(null);
  /** Gap the dragged question will land in; see FormDragDropService. */
  readonly dropGapIndex = signal<number | null>(null);
  readonly hoveredQuestionId = signal<string | null>(null);

  /**
   * Computed signal to track all reportable questions that need to be saved to the form element library
   * Only includes new elements (not from existing library) that are marked as reportable
   */
  readonly reportableQuestions = computed(() => {
    return this.questions().filter(q => q.isReportable && q.isNewElement);
  });

  // Store signals for syncing (read directly from the signal store)
  private readonly storeTitle = this.formStore.formTitle;
  private readonly storeDescription = this.formStore.formDescription;
  private readonly storeVersion = this.formStore.formVersion;
  private readonly storeShowQuestionNumbers = this.formStore.showQuestionNumbers;
  private readonly storeStatus = this.formStore.formStatus;
  // Questions stay debounced (100ms) to avoid effect thrashing during rapid batch updates.
  private readonly storeQuestions = toSignal(
    toObservable(this.formStore.questions).pipe(debounceTime(100)),
    { initialValue: [] as QuestionView[] }
  );
  private readonly storeIsReadOnly = this.formStore.isReadOnly;
  private readonly storeFormType = this.formStore.formType;

  private formId: string = 'default-form'; // TODO: Get from route or generate unique ID
  private savedTemplateId: string | null = null; // ID of the saved template in the API
  /**
   * The template an unsaved clone was copied from. Sent with the create that
   * first stores it, and cleared once it has been, so later saves are ordinary
   * updates.
   */
  private clonedFromId: string | null = null;
  private currentVersion: number = 0; // Current version number of the saved template

  // Subject to resolve canDeactivate when user makes a choice in the dialog
  private deactivateSubject = new Subject<boolean>();

  // Expose FormStatus enum to template
  FormStatus = FormStatus;
  // Expose QuestionType enum to template
  QuestionType = QuestionType;

  /**
   * Computed signal: Check if the form has any actual form elements (excluding structural elements)
   */
  readonly hasFormElements = computed(() => {
    return this.questions().some(question =>
      question.questionType !== QuestionType.Section &&
      question.questionType !== QuestionType.Title &&
      question.questionType !== QuestionType.HelperText
    );
  });

  /** Callbacks for QuestionManagementService */
  private readonly questionCallbacks: QuestionManagementCallbacks = {
    getQuestions: () => this.questions(),
    setQuestions: (qs) => this.questions.set(qs),
    isReadOnly: () => this.isReadOnly(),
    setPaletteQuestionId: (id) => this.paletteQuestionId.set(id),
    setShowPalette: (show) => this.showPalette.set(show)
  };

  /** Callbacks for FormLoadService */
  private readonly formLoadCallbacks: FormLoadCallbacks = {
    onFormIdChange: (id) => { this.formId = id; },
    onSavedTemplateIdChange: (id) => { this.savedTemplateId = id; },
    onCurrentVersionChange: (v) => { this.currentVersion = v; },
    onFormTitleChange: (title) => this.formTitle.set(title),
    onFormDescriptionChange: (desc) => this.formDescription.set(desc),
    onFormVersionChange: (v) => this.formVersion.set(v),
    onFormStatusChange: (s) => this.formStatus.set(s),
    onShowQuestionNumbersChange: (show) => this.showQuestionNumbers.set(show),
    onQuestionsChange: (qs) => this.questions.set(qs),
    onAssessmentMatrixIdsChange: (ids) => this.assessmentMatrixIds.set(ids),
    onPromotionChange: (promotion) => this.promotion.set(promotion),
    onClonedFromChange: (templateId) => { this.clonedFromId = templateId; },
    onReadOnlyChange: (ro) => this.isReadOnly.set(ro),
    // A pristine load establishes the "no changes yet" baseline; a clone is
    // brand-new unsaved content, so leave the baseline unset (dirty).
    onLoadComplete: (source) => {
      this.savedSnapshot.set(source === 'load' ? this.captureSnapshot() : null);
    }
  };

  /**
   * Serialised form state as of the last load/save. Compared against the
   * live state to decide whether the unsaved-changes prompts should fire.
   * Null means "no clean baseline" (new/cloned form) — falls back to a
   * has-any-content check.
   */
  private readonly savedSnapshot = signal<string | null>(null);

  /** Drives the toolbar's "Unsaved changes" / "Saved" note. */
  readonly unsavedChanges = computed(() => this.hasUnsavedChanges());

  constructor() {
    // Effect to sync store signals → writable signals (replaces manual subscriptions)
    effect(() => {
      this.formTitle.set(this.storeTitle());
      this.formDescription.set(this.storeDescription());
      this.formVersion.set(this.storeVersion());
      this.showQuestionNumbers.set(this.storeShowQuestionNumbers());
      const status = this.storeStatus();
      if (status) this.formStatus.set(status);
      this.questions.set(this.storeQuestions());
      this.isReadOnly.set(this.storeIsReadOnly());
      this.formType.set((this.storeFormType() as FormType | null) ?? null);
      this.cdr.markForCheck();
    });

    // Effect to auto-save form state to localStorage when form data changes
    effect(() => {
      // Access signals to track them
      this.formTitle();
      this.formDescription();
      this.formVersion();
      this.showQuestionNumbers();
      this.formStatus();
      this.questions();

      // Save to localStorage (debounced by the effect system)
      this.formPersistenceService.saveForm();
    });
  }

  private static readonly SESSION_ALIVE_KEY = 'form-builder-session-alive';

  ngOnInit(): void {
    // Check for session recovery state from navigation
    const navigation = this.router.getCurrentNavigation();
    const sessionRecoveryState = navigation?.extras?.state?.['sessionRecovery'] ||
                                  history.state?.['sessionRecovery'];

    // Detect fresh browser open vs page refresh.
    // sessionStorage persists across refreshes but is cleared when the browser/tab is closed.
    // If the session flag is missing, this is a fresh browser session — clear any stale form data.
    const isExistingSession = sessionStorage.getItem(FormBuilderComponent.SESSION_ALIVE_KEY) === 'true';
    if (!isExistingSession) {
      this.formPersistenceService.clearForm();
    }
    // Mark the session as alive (persists across refreshes, cleared on browser/tab close)
    sessionStorage.setItem(FormBuilderComponent.SESSION_ALIVE_KEY, 'true');

    // First, determine if we have a saved form or need to initialize a new one
    const savedForm = this.formPersistenceService.loadForm();

    // Check if saved form is essentially empty (no meaningful content)
    const isEmptyForm = savedForm && this.isFormEmpty(savedForm);

    // Always initialize the store first
    this.formStore.initializeForm(this.formId);

    // Check if this is a session recovery scenario
    if (sessionRecoveryState && savedForm && !isEmptyForm) {
      // Store recovery info and show recovery dialog
      this.sessionRecoveryInfo = sessionRecoveryState;
      this.formId = savedForm.id;
      this.loadForm(savedForm, false);
      // Show session recovery dialog instead of welcome dialog
      this.showSessionRecoveryDialog.set(true);
      // Clear the navigation state so it doesn't show again on refresh
      history.replaceState({}, '');
    } else if (savedForm && !isEmptyForm) {
      // Normal load saved form into store (only if it has meaningful content)
      this.formId = savedForm.id;
      this.loadForm(savedForm, false);
    } else {
      // Clear any empty saved form data
      if (isEmptyForm) {
        this.formPersistenceService.clearForm();
      }
    }
  }

  /**
   * Warn the user before closing/refreshing the browser if there are unsaved changes.
   * Shows the browser's native "Leave site?" confirmation dialog.
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    // Session expired: local work has been cleared on purpose, so don't
    // block the redirect to login with a "Leave site?" prompt.
    if (this.sessionTimeout.isRedirecting()) return;
    if (this.hasUnsavedChanges()) {
      // Trigger browser's native confirmation dialog
      event.preventDefault();
    }
  }

  handleSort(): void {
    // Sort functionality handled by drag and drop
  }

  onQuestionRowMouseEnter(questionId: string, _isLast: boolean): void {
    this.hoveredQuestionId.set(questionId);
  }

  onQuestionRowMouseLeave(questionId: string, event?: MouseEvent): void {
    // Check if we're moving to a child element (like the menu)
    if (event && event.relatedTarget) {
      const relatedTarget = event.relatedTarget as HTMLElement;
      const questionRow = event.currentTarget as HTMLElement;

      // If moving to a child element within the question row, don't clear hover
      if (questionRow && questionRow.contains(relatedTarget)) {
        return;
      }

      // Also check if we're moving to the context menu specifically
      // The menu might be positioned outside the question-row-content but still part of the row
      const questionRowElement = document.querySelector(`[data-question-id="${questionId}"]`);
      if (questionRowElement && questionRowElement.contains(relatedTarget)) {
        return;
      }
    }

    // Use a small delay to prevent flickering when moving between elements
    // This helps when the mouse moves quickly between the row and menu
    setTimeout(() => {
      // Double-check that we're still not hovering over the question row or menu
      const questionRowElement = document.querySelector(`[data-question-id="${questionId}"]`);
      if (questionRowElement) {
        // Check if any element within the question row is being hovered
        const hoveredElement = questionRowElement.querySelector(':hover');
        if (!hoveredElement && this.hoveredQuestionId() === questionId) {
          this.hoveredQuestionId.set(null);
        }
      } else if (this.hoveredQuestionId() === questionId) {
        this.hoveredQuestionId.set(null);
      }
    }, 100);
  }

  isQuestionMenuVisible(question: QuestionView, _isLast: boolean): boolean {
    if (this.isReadOnly()) {
      return false;
    }
    // A question being edited always shows its menu so the Accept tick is
    // discoverable without hovering — critical on tall questions where the
    // hover-only menu is easy to lose.
    if (question.isEditing) {
      return true;
    }
    return this.hoveredQuestionId() === question.id;
  }

  handleDelete(questionId: string): void {
    if (this.isReadOnly()) return;
    this.questionManagement.deleteQuestion(questionId);
  }

  handleInsertBelow(questionId: string): void {
    if (this.isReadOnly()) return;
    this.questionManagement.insertBelow(questionId, this.questionCallbacks);
  }

  handleAddToRow(questionId: string): void {
    alert('Add to question ' + questionId);
  }

  handleAddNew(): void {
    if (this.isReadOnly()) return;
    this.questionManagement.addNew(this.questionCallbacks);
  }

  toggleRequired(questionId: string): void {
    if (this.isReadOnly()) return;
    this.questionManagement.toggleRequired(questionId, this.questions());
  }

  onDragStart(event: DragEvent, questionId: string): void {
    this.dragDropService.onDragStart(event, questionId);
    this.draggedQuestionId.set(this.dragDropService.draggedQuestionId());
  }

  onDragOver(event: DragEvent, index: number): void {
    this.dragDropService.onDragOver(event, index);
    this.dragOverIndex.set(this.dragDropService.dragOverIndex());
    this.dropGapIndex.set(this.dragDropService.dropGapIndex());
  }

  onDragLeave(): void {
    this.dragDropService.onDragLeave();
    this.dragOverIndex.set(null);
    this.dropGapIndex.set(null);
  }

  onDrop(event: DragEvent, dropIndex: number): void {
    this.dragDropService.onDrop(
      event, dropIndex, this.questions(),
      (qs) => this.recalculateQuestionNumbers(qs),
      (qs) => this.questions.set(qs),
      this.ngZone, this.cdr
    );
    this.onDragEnd();
  }

  /**
   * Clears the drag state. Called on drop and when a drag is abandoned: the
   * drop target's highlight is driven from `dragOverIndex`, so leaving it set
   * leaves a row looking like a live drop target.
   */
  onDragEnd(): void {
    this.draggedQuestionId.set(null);
    this.dragOverIndex.set(null);
    this.dropGapIndex.set(null);
  }

  trackByQuestionId(index: number, question: QuestionView): string {
    return question.id;
  }
  /**
   * Recalculate question numbers and section IDs for all questions
   * Delegates to QuestionNumberingService
   */
  private recalculateQuestionNumbers(questions: QuestionView[]): QuestionView[] {
    return this.questionNumberingService.recalculateQuestionNumbers(questions);
  }

  onShowQuestionNumbersChange(value: boolean): void {
    this.showQuestionNumbers.set(value);
    this.formStore.toggleShowQuestionNumbers(value);
  }

  onClearForm(): void {
    // Clear localStorage when form is cleared
    this.formPersistenceService.clearForm();
    // Nothing of the clone is left, so it is no longer a copy of anything.
    this.clonedFromId = null;
    this.formStore.updateClonedFrom(null);
    // Clear the form by reinitializing it with the current form ID (don't regenerate)
    // Only cloning should regenerate the form ID
    this.formStore.initializeForm(this.formId);

    // Reset local signals
    this.formTitle.set('');
    this.formDescription.set('');
    this.showQuestionNumbers.set(true);
    this.formStatus.set(FormStatus.Draft);
    this.formType.set(null);
    this.questions.set([]);
    this.assessmentMatrixIds.set([]);
    this.promotion.set(null);
    this.savedTemplateId = null;
    this.currentVersion = 0;
    this.formVersion.set(0);
    // A cleared form has nothing worth protecting: drop the baseline from the
    // form that was open, or the empty form reads as unsaved changes against it.
    this.savedSnapshot.set(null);
  }

  /**
   * Handle session recovery dialog - user wants to continue editing
   */
  onSessionRecoveryConfirmed(): void {
    // Close dialog and clear the session recovery flag
    this.showSessionRecoveryDialog.set(false);
    this.formPersistenceService.clearSessionRecovery();
    this.sessionRecoveryInfo = null;

    // Show a toast to confirm the form was restored
    this.messageService.add({
      severity: 'success',
      summary: 'Form Restored',
      detail: 'Your previous form has been restored. You can continue editing.',
      life: 4000
    });
  }

  /**
   * Handle session recovery dialog - user wants to start fresh
   */
  onSessionRecoveryCancelled(): void {
    // Close dialog and clear both form and recovery flag
    this.showSessionRecoveryDialog.set(false);
    this.formPersistenceService.clearFormAndRecovery();
    this.sessionRecoveryInfo = null;

    this.onClearForm();
  }

  /**
   * Get session recovery form title for display in dialog
   */
  getSessionRecoveryFormTitle(): string {
    return this.sessionRecoveryInfo?.formTitle || 'Untitled Form';
  }

  onElementSelected(element: FormElement): void {
    if (this.isReadOnly()) return;

    // Handle existing form elements
    if (element.type === 'existing' && element.existingElementData) {
      this.questionManagement.onExistingElementSelected(
        element.existingElementData,
        element.id, element.name, element.labelText,
        this.questionCallbacks, this.paletteQuestionId(),
        this.ngZone, this.cdr
      );
      return;
    }

    // Handle new element type selection
    this.questionManagement.onNewElementSelected(
      element.id, element.isReportable || false, element.isNewElement || true,
      this.questionCallbacks, this.paletteQuestionId(),
      this.ngZone, this.cdr
    );
  }

  onQuestionChange(updatedQuestion: QuestionView): void {
    this.formStore.updateQuestion(updatedQuestion);
  }

  onQuestionConfigChange(questionId: string, config: Partial<QuestionView>): void {
    const question = this.questions().find(q => q.id === questionId);
    if (question) {
      this.formStore.updateQuestion({ ...question, ...config });
    }
  }

  onPaletteClose(): void {
    this.showPalette.set(false);
    this.paletteQuestionId.set(null);
  }

  onPaletteCancelled(): void {
    this.questionManagement.cancelPalette(this.questionCallbacks, this.paletteQuestionId());
  }

  acceptQuestion(questionId: string): void {
    this.questionManagement.acceptQuestion(questionId, this.questions());
  }

  editQuestion(questionId: string): void {
    this.questionManagement.editQuestion(questionId, this.questions());
  }

  private buildFormSaveContext(): FormSaveContext {
    return {
      formId: this.formId,
      savedTemplateId: this.savedTemplateId,
      currentVersion: this.currentVersion,
      formVersion: this.formVersion(),
      formTitle: this.formTitle(),
      formDescription: this.formDescription(),
      showQuestionNumbers: this.showQuestionNumbers(),
      questions: this.questions(),
      assessmentMatrixIds: this.assessmentMatrixIds(),
      clonedFromId: this.clonedFromId,
      messageService: this.messageService,
      // Routes the save: 'Application' → Application API, others → Forms API.
      formType: (this.formType() ?? 'Application') as FormSaveContext['formType']
    };
  }

  private handleFormSaveSuccess(result: FormSaveResult): void {
    this.savedTemplateId = result.savedTemplateId;
    // Provenance has been recorded; from here the form is saved, not a clone.
    this.clonedFromId = null;
    this.formStore.updateClonedFrom(null);
    this.currentVersion = result.currentVersion;
    this.formVersion.set(result.currentVersion);
    if (result.savedTemplateId) {
      this.formId = result.savedTemplateId;
    }
    // The just-saved state is the new clean baseline for the unsaved-changes checks.
    this.savedSnapshot.set(this.captureSnapshot());
    this.reportableElements.saveReportableElements(
      this.reportableQuestions(), this.messageService,
      (ids) => this.reportableElements.markElementsAsSaved(
        ids, () => this.questions(), (qs) => this.questions.set(qs)
      )
    );
  }

  saveDraft(): void {
    this.formSaveService.saveDraft(
      this.buildFormSaveContext(),
      (result) => this.handleFormSaveSuccess(result)
    );
  }

  publishForm(): void {
    this.formSaveService.publishForm(
      this.buildFormSaveContext(),
      (result) => this.handleFormSaveSuccess(result)
    );
  }

  onFormTitleChange(title: string): void {
    this.formStore.updateFormTitle(title);
  }

  onFormDescriptionChange(description: string): void {
    this.formStore.updateFormDescription(description);
  }

  onFormVersionChange(version: number): void {
    this.formStore.updateFormVersion(version);
  }

  /**
   * Handle archive form action from header - show confirmation dialog
   */
  onArchiveForm(): void {
    this.showArchiveConfirmation.set(true);
  }

  /**
   * Handle confirmed archive action - save to server with Archived status
   */
  archiveForm(): void {
    this.formSaveService.archiveForm(
      this.buildFormSaveContext(),
      (result) => this.handleFormSaveSuccess(result)
    );
  }

  /**
   * Handle "Saved Forms" button click from toolbar.
   */
  openFormSelector(): void {
    this.confirmIfUnsavedChanges(() => {
      this.formSelectorMode.set('open');
      this.showFormSelector.set(true);
    });
  }

  /**
   * Pick a saved form to show beside this one. Nothing is replaced, so there's
   * no unsaved-changes prompt.
   */
  openCompareSelector(): void {
    this.formSelectorMode.set('compare');
    this.showFormSelector.set(true);
  }

  /**
   * Ask for this version to go to the next environment. The client's sign-off
   * is what actually sends it, so this records the request and reports back.
   */
  promoteToProduction(): void {
    const templateId = this.savedTemplateId ?? this.formId;
    if (!templateId) return;

    this.formApiService.requestPromotion(templateId).subscribe({
      next: template => {
        this.promotion.set(template.promotion ?? null);
        this.messageService.add({
          severity: 'success',
          summary: 'Promotion Requested',
          detail: `This version is waiting for sign-off before it goes to ${promotionTargetLabel(template.promotion?.targetEnvironment)}.`
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Promotion Failed',
          detail: 'This version could not be sent for promotion. Please try again.'
        });
      }
    });
  }

  /**
   * Record the client's sign-off, which is what sets the worker copying. The
   * client approves outside the app, so this is a statement about something
   * that already happened — hence the confirmation.
   */
  approvePromotion(): void {
    const templateId = this.savedTemplateId ?? this.formId;
    if (!templateId) return;

    this.formApiService.approvePromotion(templateId).subscribe({
      next: template => {
        this.promotion.set(template.promotion ?? null);
        this.messageService.add({
          severity: 'success',
          summary: 'Promotion Approved',
          detail: `This version is on its way to ${promotionTargetLabel(template.promotion?.targetEnvironment)}.`
        });
      },
      error: error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Approval Failed',
          // The API refuses an approval whose template has changed since the
          // request, which is worth saying rather than hiding.
          detail: error?.error?.detail ?? 'This version could not be approved. Please try again.'
        });
      }
    });
  }

  closeCompare(): void {
    this.compareTemplate.set(null);
  }

  /**
   * A form type chosen from the menu under New. The new form replaces the
   * open one, so unsaved changes are confirmed at this point.
   */
  onCreateFormClick(formType: FormType): void {
    this.confirmIfUnsavedChanges(() => this.onFormTypeSelected(formType));
  }

  /**
   * Executes an action immediately if no unsaved changes, otherwise shows confirmation dialog.
   * This is the single entry point for all actions that may discard unsaved work.
   */
  private confirmIfUnsavedChanges(action: () => void): void {
    if (this.hasUnsavedChanges()) {
      this.pendingAction = action;
      this.showUnsavedChangesDialog.set(true);
    } else {
      action();
    }
  }

  /**
   * Handle form type selection from the form type selector dialog.
   * Initializes a new form with the selected type.
   */
  onFormTypeSelected(formType: FormType): void {
    console.log('Form type selected:', formType);

    // Clear any existing form and initialize a new one
    this.formPersistenceService.clearForm();
    this.formId = generateGuid();
    this.savedTemplateId = null;
    this.clonedFromId = null;

    // Initialize new form in store, recording which backend owns it.
    this.formStore.initializeForm(this.formId);
    this.formStore.updateFormType(formType as FormTemplateType);

    // Reset local state
    this.questions.set([]);
    this.formTitle.set('');
    this.formDescription.set('');
    this.formVersion.set(0);
    this.formStatus.set(FormStatus.Draft);
    this.formType.set(formType);
    this.isReadOnly.set(false);

    // Set a default title based on the form type
    const defaultTitles: Record<FormType, string> = {
      [FormType.Application]: 'New Application Form',
      [FormType.Assessment]: 'New Assessment Form',
      [FormType.EligibilityQuestionnaire]: 'New Eligibility Questionnaire',
      [FormType.PreAssessment]: 'New Pre-assessment Form',
      [FormType.Survey]: 'New Survey',
      [FormType.UserProfile]: 'New User Profile Form'
    };

    this.formStore.updateFormTitle(defaultTitles[formType]);
    this.formTitle.set(defaultTitles[formType]);

    // A freshly initialised form is clean — only actual edits should trip
    // the unsaved-changes prompts.
    this.savedSnapshot.set(this.captureSnapshot());

    this.messageService.add({
      severity: 'success',
      summary: 'New Form Created',
      detail: `Started creating a new ${formType.replace(/([A-Z])/g, ' $1').trim()}`
    });
  }

  /**
   * Check if a form object is essentially empty (no meaningful content).
   * Used to decide whether a form saved in this browser is worth restoring.
   */
  private isFormEmpty(form: Form): boolean {
    const hasQuestions = form.questions && form.questions.length > 0;
    const hasTitle = form.title && form.title.trim() !== '';
    return !hasQuestions && !hasTitle;
  }

  onFormSelected(templateDto: ApplicationTemplateDto): void {
    this.setFormTypeFromDto(templateDto);
    this.formLoadService.onFormSelected(templateDto, this.formLoadCallbacks, this.ngZone, this.cdr);
  }

  onFormCloned(templateDto: ApplicationTemplateDto): void {
    this.setFormTypeFromDto(templateDto);
    this.formLoadService.onFormCloned(templateDto, this.formLoadCallbacks, this.ngZone, this.cdr);
  }

  private setFormTypeFromDto(templateDto: ApplicationTemplateDto): void {
    this.formType.set(templateDto.formTemplateType ? templateDto.formTemplateType as FormType : null);
  }

  /** Used by ngOnInit to load a cached form directly */
  private loadForm(form: Form, readOnly: boolean): void {
    this.formLoadService.loadForm(form, readOnly, this.formLoadCallbacks, this.ngZone, this.cdr);
  }

  // ============================================================
  // Navigation Guard Methods
  // ============================================================

  /**
   * Serialise the parts of form state a user can edit. UI-only per-question
   * fields (menu visibility, edit mode) are stripped so opening a question
   * for editing doesn't count as a change.
   */
  private captureSnapshot(): string {
    const questions = this.questions().map(q => {
      const { showMenu, isEditing, ...persisted } = q;
      return persisted;
    });
    return JSON.stringify({
      title: this.formTitle(),
      description: this.formDescription(),
      version: this.formVersion(),
      showQuestionNumbers: this.showQuestionNumbers(),
      formType: this.formType(),
      assessmentMatrixIds: this.assessmentMatrixIds(),
      questions
    });
  }

  /**
   * Check if the form has unsaved changes.
   * With a clean baseline (set on load and after each successful save), this
   * is a real dirty-check: current state vs baseline. Without one (new or
   * cloned form), any content counts as unsaved.
   * Read-only forms (view mode) never have unsaved changes.
   */
  private hasUnsavedChanges(): boolean {
    if (this.isReadOnly()) {
      return false;
    }

    const savedSnapshot = this.savedSnapshot();
    if (savedSnapshot !== null) {
      return this.captureSnapshot() !== savedSnapshot;
    }

    const hasQuestions = this.questions().length > 0;
    const hasTitle = this.formTitle() && this.formTitle().trim() !== '';
    const hasFormType = this.formType() !== null;
    return hasQuestions || hasTitle || hasFormType;
  }

  /**
   * CanDeactivate guard implementation.
   * Shows the unsaved changes dialog if there are unsaved changes.
   */
  canDeactivate(): Observable<boolean> | boolean {
    if (!this.hasUnsavedChanges()) {
      return true;
    }

    // Set pending action for route navigation
    this.pendingAction = () => this.deactivateSubject.next(true);
    this.showUnsavedChangesDialog.set(true);

    // Return an observable that will emit when the user makes a choice
    return this.deactivateSubject.asObservable().pipe(take(1));
  }

  // =========================================================================
  // Unsaved Changes Dialog Handlers
  // =========================================================================

  /**
   * Handle "Save" action from unsaved changes dialog.
   * Saves the form and proceeds with the pending action.
   */
  onUnsavedChangesSave(): void {
    this.saveDraft();
    this.closeDialogAndExecutePendingAction();
  }

  /**
   * Handle "Don't Save" action from unsaved changes dialog.
   * Discards changes and proceeds with the pending action.
   */
  onUnsavedChangesDontSave(): void {
    this.clearFormState();
    this.closeDialogAndExecutePendingAction();
  }

  /**
   * Handle "Cancel" action from unsaved changes dialog.
   * Closes dialog and stays on current form.
   */
  onUnsavedChangesCancel(): void {
    this.showUnsavedChangesDialog.set(false);
    // Notify route guard that navigation was cancelled
    this.deactivateSubject.next(false);
    this.pendingAction = null;
  }

  // =========================================================================
  // Private Helper Methods
  // =========================================================================

  /**
   * Closes the unsaved changes dialog and executes the pending action.
   */
  private closeDialogAndExecutePendingAction(): void {
    this.showUnsavedChangesDialog.set(false);
    const action = this.pendingAction;
    this.pendingAction = null;
    action?.();
  }

  /**
   * Resets the form to a clean state.
   */
  private clearFormState(): void {
    this.formPersistenceService.clearForm();
    this.formStore.initializeForm(this.generateFormId());
    this.formTitle.set('');
    this.formType.set(null);
    this.formStatus.set(FormStatus.Draft);
    this.assessmentMatrixIds.set([]);
    this.promotion.set(null);
    this.savedTemplateId = null;
    this.isReadOnly.set(false);
    // Cleared form has nothing worth protecting.
    this.savedSnapshot.set(null);
  }

  /**
   * Generates a unique form ID.
   */
  private generateFormId(): string {
    return `form-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
}

export default FormBuilderComponent

